import View from '@play-co/timestep-core/lib/ui/View';
import ImageScaleView, {
  ImageScaleViewOpts,
} from '@play-co/timestep-core/lib/ui/ImageScaleView';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import uiConfig from 'src/lib/ui/config';
import bitmapFonts from 'src/lib/bitmapFonts';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';

export type PopupBasicTitleType = 'default' | 'alt' | 'minimal' | 'none';

export default class PopupBasicTitle {
  public readonly root: View;
  protected icon: View | undefined;
  protected placeholderIcon: View | undefined;
  public banner: ImageScaleView;
  public title: LangBitmapFontTextView;
  public titleContainer: FlexBoxView;

  constructor(
    protected opts: {
      superview: View;
      type?: PopupBasicTitleType;
      icon?: ImageScaleViewOpts;
      banner?: ImageScaleViewOpts;
    },
  ) {
    const { superview } = opts;

    this.root = new View({
      superview,
      zIndex: 998,
      width: superview.style.width,
      height: 80,
    });

    this.banner = new ImageScaleView({
      ...uiConfig.popups.titleBanner,
      ...opts.banner,
      superview: this.root,
      x: this.root.style.width / 2,
    });

    let titleSkin: any;
    if (opts.type === 'minimal') {
      titleSkin = uiConfig.popups.titleMinimal;
    } else {
      titleSkin = uiConfig.popups.title;
    }

    this.titleContainer = new FlexBoxView({
      superview: this.root,
      x: 108,
      y: this.banner ? titleSkin.y + this.banner.style.y : titleSkin.y,
      width: this.root.style.width - 225,
      height: 104,
      centerAnchor: true,
      flex: {
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    this.title = new LangBitmapFontTextView({
      ...titleSkin,
      superview: this.titleContainer,
      width: this.root.style.width - 220,
      height: 104,
      font: bitmapFonts(titleSkin.fontName),
      size: titleSkin.size,
    });

    if (opts.icon) {
      this.setIcon(opts.icon);
    }
  }

  public getView() {
    return this.root;
  }

  public updateOpts(opts: any) {
    if (this.title) this.title.updateOpts(opts);
    return opts;
  }

  public setText(value: () => string) {
    // Update text.
    this.title.updateOpts({
      width: this.root.style.width - 220,
      localeText: value,
    });

    // Adjust yoga width to actual text width.
    getFlexNode(this.title).width = this.title.getTextWidth();
  }

  public setIcon(opts: ImageScaleViewOpts | View) {
    const isView = 'style' in opts;
    const iconOpts = isView ? opts.style : opts;

    // Create icon if it doesn't exist.
    if (!this.icon) {
      // Configure icon options.
      this.icon = !isView
        ? new ImageScaleView({
            width: this.titleContainer.style.height * 0.8,
            height: this.titleContainer.style.height * 0.7,
            scaleMethod: 'contain',
          })
        : opts;
      const iconNode = getFlexNode(this.icon);
      iconNode.flexShrink = 0;
      this.titleContainer.addSubview(this.icon, this.title);

      // Create an invisible icon on the right side.
      // This will make sure the text is centered when possible.
      // Otherwise  this container will shrink, and the text will be offset to the right.
      this.placeholderIcon = new View({
        superview: this.titleContainer,
        width: this.titleContainer.style.height * 0.8,
        height: this.titleContainer.style.height * 0.7,
        opacity: 0,
      });
      const placeholderNode = getFlexNode(this.placeholderIcon);
      placeholderNode.flexShrink = 5;

      // This will make sure the text is centered when possible.
      getFlexNode(this.titleContainer).justifyContent = 'space-between';
    }

    // Update icon opts.
    this.icon.updateOpts({
      ...iconOpts,
    });
    this.placeholderIcon!.updateOpts({
      ...iconOpts,
      opacity: 0,
    });

    // Update yoga width and height.
    const yogaOpts: any = {};
    if (iconOpts.width) yogaOpts.width = iconOpts.width;
    if (iconOpts.height) yogaOpts.height = iconOpts.height;
    getFlexNode(this.icon).updateStyle(yogaOpts);
    getFlexNode(this.placeholderIcon!).updateStyle(yogaOpts);
  }

  public setBanner(bannerOpts: ImageScaleViewOpts) {
    this.banner.updateOpts(bannerOpts);
  }
}
